<template>
  <div class="comments-page-wrapper">
    <MediaTaggingNavigationPanel />
    <div class="comments-wrapper">
      <CustomSearch placeholder="Find..." @update:modelValue="keySearch = $event" />
      <div class="scrollable-container">
        <div
          v-for="(comment, index) in comments"
          :key="index"
          class="comment-item"
          :class="{ active: currentIndex === index}"
          @click="changeActiveComment(comment.messageId, index)"
        >
          <TaggingComment
            :key="index"
            :comment="comment"
            :comment-search="keySearch"
            @changeComment="handleChangeComment"
            @deleteComment="handleDeleteComment"
          />
        </div>
        <infinite-loading :identifier="infiniteId" @infinite="infiniteHandler">
          <div slot="spinner">
            Loading...
          </div>
          <div slot="no-more">
            No more messages
          </div>
          <div slot="no-results">
            No results
          </div>
        </infinite-loading>
      </div>
    </div>
    <MediaTaggingPanel
      :update-media="updateId"
      @updateComments="updateComments"
      @openMediaPopup="openMediaPopup"
    />
    <CommonDialog ref="commentDeleteDialog">
      <BlueBigSuccessIcon />
      <h2 class="dialog-message-title">
        Success!
      </h2>
      <p class="dialog-message-description" style="display:block;">
        The comment was deleted
      </p>
    </CommonDialog>
    <MediaItemPopup
      ref="mediaItemPopup"
      :attachment="currentAttachment"
      :attachment-type="currentAttachmentType"
      :tags-list="currentTagsList"
      :unblurred-link="unblurredLink"
      :gallery-items="galleryItems"
      :current-index="currentIndex"
      @closed="unblurredLink = ''"
      @menuOptionSelect="handleMenuOptionSelect"
      @showOriginContent="showOriginContent"
      @pageChanged="handlePageChanged"
    />
    <MetadataPopup ref="metadataPopup" :file-metadata="fileMetadata" />
    <CommonDialog
      ref="progressDialog"
      :show-footer="false"
      :closable="false"
    >
      <h2 class="dialog-message-title">
        Exporting media
      </h2>
      <p class="dialog-create-description" style="display:block; font-size: 14px;">
        Please wait a bit. The media is being exported.
      </p>
      <div class="loader-p" />
    </CommonDialog>
    <SuccessPopup
      ref="successPopup"
      text="A URL will be sent to your email with a download link. Do not share this link."
    />
  </div>
</template>

<script>
import MediaTaggingNavigationPanel from '@/components/cases/case-messages/media-tags/MediaTaggingNavigationPanel.vue';
import MediaTaggingPanel from '@/components/cases/case-messages/media-tags/MediaTaggingPanel.vue';
import CustomSearch from '@/components/inputs/CustomSearch.vue';
import TaggingComment from '@/components/cases/case-messages/tagging-common/TaggingComment.vue';
import InfiniteLoading from 'vue-infinite-loading';
import CommonDialog from '@/components/popups/CommonDialog.vue';
import mediaTagging from '@/mixins/mediaTagging';
import streamReader from '@/mixins/streamReader';
import BlueSuccessIcon94 from '@/components/icons/BlueSuccessIcon94.vue';
import MetadataPopup from '@/components/popups/MetadataPopup.vue';
import MediaItemPopup from '@/components/popups/MediaItemPopup.vue';
import SuccessPopup from '@/components/popups/SuccessPopup.vue';
import {mapActions, mapGetters} from 'vuex';

export default {
  name: 'MediaTaggingCommentsTab',
  components: {
    MediaItemPopup, MetadataPopup,
    BlueBigSuccessIcon: BlueSuccessIcon94,
    MediaTaggingNavigationPanel,
    CommonDialog,
    TaggingComment,
    CustomSearch,
    MediaTaggingPanel,
    InfiniteLoading,
    SuccessPopup,
  },
  mixins: [streamReader, mediaTagging],
  data() {
    return {
      infiniteId: +new Date(),
      updateId: +new Date(),
      currentIndex: 0,

      currentAttachment: {},
      currentDeviceInfo: {},
      currentAttachmentType: '',
      currentTagsList: [],
      unblurredLink: '',
      galleryItems: [],
      fileMetadata: null,
      selectedAttachmentsIds: [],
      format: '',
    };
  },
  computed: {
    ...mapGetters('mediaTagging', {
      isTaggingActive: 'isTaggingActive',
    })
  },
  watch: {
    keySearch() {
      this.updateComments();
    },
  },
  methods: {
    ...mapActions('mediaTagging', ['setInitialState']),
    updateComments() {
      this.comments = [];
      this.page = 1;
      this.infiniteId += 1;
    },
    changeActiveComment(galleryId, index) {
      this.currentIndex = index;
      this.galleryId = galleryId;
      this.getMediaByGalleryId(galleryId);
    },

    async infiniteHandler($state) {
      await this.getListCommentsByClientId(
        $state,
        this.clientId,
        {
          page: this.page,
          size: this.pageSize,
          searchKey: this.keySearch
        }
      );
    },

    async handleChangeComment(commentId, newText) {
      await this.editComment(commentId, {text: newText});
      await this.getListComments();
      this.updateId += 1;
    },

    async handleDeleteComment(commentId) {
      await this.deleteComment(commentId);
      this.$refs.commentDeleteDialog.openPopups();
      await this.getListComments();
      this.updateId += 1;
    },

    openMediaPopup(itemId) {
      this.$load(async () => {
        try {
          const response = await this.$api.media.getGalleryById(
            this.caseId,
            this.clientId,
            itemId
          );

          const mediaItem = response.data.body;

          this.galleryItems = [mediaItem];
          this.currentIndex = 0;
          this.currentAttachment = mediaItem;

          this.currentAttachmentType = this.currentAttachment.type
            ? this.currentAttachment.type.split('/')[0].toLowerCase()
            : this.getFileType(this.currentAttachment.name);
          this.currentTagsList = this.currentAttachment.tags || [];

          this.$refs.mediaItemPopup.openPopups();
        } catch (error) {
          this.handleResponseError(error);
        }
      });
    },

    handleMenuOptionSelect(optionName) {
      switch (optionName.toLocaleLowerCase()) {
        case 'export':
          this.openExportPopup(this.currentAttachment.id);
          break;
        case 'mark priority':
        case 'remove from priorities':
          if (!this.currentAttachment.isPinned) {
            this.pinGallery([this.currentAttachment.id]).then(() => {
              this.updateAttachmentInPopup();
            });
          } else {
            this.unPinGallery([this.currentAttachment.id]).then(() => {
              this.updateAttachmentInPopup();
            });
          }
          this.updateId += 1;
          break;
        case 'redact media':
        case 'unredact media':
          if (!this.currentAttachment.isBlured) {
            this.blurGallery(this.currentAttachment.id).then(() => {
              this.updateAttachmentInPopup();
            });
          } else {
            this.unBlurGallery(this.currentAttachment.id).then(() => {
              this.updateAttachmentInPopup();
            });
          }
          this.updateId += 1;
          break;
        case 'show meta':
          this.openMetadataPopup();
          this.$refs.mediaItemPopup.closePopups();
          break;
        case 'view details':
          this.gotoTagging();
          this.$refs.mediaItemPopup.closePopups();
          break;
      }
    },

    updateAttachmentInPopup() {
      this.$load(async () => {
        const response = await this.$api.media.getGalleryById(
          this.caseId,
          this.clientId,
          this.currentAttachment.id
        );
        this.currentAttachment = response.data.body;
        this.currentTagsList = this.currentAttachment.tags || [];

        const index = this.galleryItems.findIndex(item => item.id === this.currentAttachment.id);
        if (index !== -1) {
          this.$set(this.galleryItems, index, this.currentAttachment);
        }
      }, (error) => {
        this.handleResponseError(error);
      });
    },

    async showOriginContent(galleryId) {
      await this.showBluredGallery(galleryId);
    },

    handlePageChanged(data) {
      this.currentIndex = data.index;
      this.currentAttachment = this.galleryItems[data.index];
      this.currentTagsList = this.currentAttachment.tags || [];
      this.currentAttachmentType = this.currentAttachment.type
        ? this.currentAttachment.type.split('/')[0].toLowerCase()
        : this.getFileType(this.currentAttachment.name);
    },

    openMetadataPopup() {
      this.$load(async () => {
        const response = await this.$api.media.getGalleryData({
          caseId: this.caseId,
          galleryId: this.currentAttachment.id
        });
        this.fileMetadata = response.data.body;
        this.$refs.metadataPopup.openPopups();
      }, (error) => {
        this.handleResponseError(error);
      });
    },

    gotoTagging() {
      if (this.isTaggingActive) {
        this.setTaggingItem(this.currentAttachment);
      } else {
        this.setInitialState({
          caseId: this.caseId,
          clientId: this.clientId,
          taggingItem: this.currentAttachment,
        });
      }
    },

    openExportPopup(data) {
      this.selectedAttachmentsIds = Array.isArray(data) ? data : [data];
      this.exportMedia();
    },

    exportMedia() {
      this.$load(async () => {
        this.$refs.progressDialog.openPopups();

        const response = await this.$api.media.exportGallery(
          this.$route.params.slug,
          this.format || 'csv',
          {galleryIds: this.selectedAttachmentsIds}
        );
        // eslint-disable-next-line no-undef
        const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
        await this.processStreamData(reader, 25);
        this.handleStreamResult();
        this.selectedAttachmentsIds = [];

        this.$refs.successPopup.openPopup();
      }, (error) => {
        this.handleResponseError(error);
      });
    },

    getFileType(fileName) {
      if (!fileName) return 'other';

      const extension = fileName.split('.').pop().toLowerCase();
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
      const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'wmv', 'flv', 'mkv'];

      if (imageExtensions.includes(extension)) {
        return 'image';
      } else if (videoExtensions.includes(extension)) {
        return 'video';
      } else {
        return 'other';
      }
    },
  },
};
</script>

<style scoped lang="scss">
.comments-page-wrapper {
  display: flex;
  position: relative;
  margin: 25px 60px 0 40px;
  max-width: 90vw;

  .comments-wrapper {
    min-width: 600px;
    width: 100%;
    padding: 50px 0 0;

    > :first-child {
      margin-bottom: 10px;
      border-bottom: 1px solid var(--case-btn-border-color);
    }

    .scrollable-container {
      height: 90vh;
      overflow-y: auto;
      scrollbar-color: var(--background-color) transparent;
      scrollbar-width: thin;
      border-top: 1px solid var(--case-btn-border-color);
      margin-top: 15px;
      padding-top: 15px;
    }
  }

  .comment-item {
    padding: 10px 25px;
    transition: all .5s;
    cursor: pointer;

    &:hover {
      background-color: var(--case-card-hover-bg);
    }
  }

  .active {
    background-color: var(--case-card-hover-bg);
  }

  .comment-textarea {
    width: 100%;
    resize: none;
    min-height: 104px;
    outline: none;
    border: 1px solid var(--input-placeholder-value-color);
    border-radius: 16px !important;
    padding: 16px 24px;
    font-size: 14px;
    font-weight: 500;
    background: transparent;
    position: relative;
    z-index: 2;
    color: var(--secondary-text-color);
    margin: 15px 0;
  }

  .btn {
    max-width: 120px;
    height: 35px;
  }
}

svg {
  cursor: pointer;
}

.dialog-message-title {
  font-style: normal;
  font-weight: bold;
  font-size: 30px;
  line-height: 36px;
  color: #292B2F;
  text-align: center;
  margin: 0;
  display: block;
  width: 100%;
  margin-top: 25px;
}

.dialog-message-description {
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 26px;
  text-align: center;
  color: #696F79;
  width: 100%;
  margin-top: 20px;
}
</style>
